import { useEffect } from 'react';
import './App.css';
import {
  BrowserRouter, Routes, Route, Navigate, useLocation,
} from 'react-router-dom';
import client from './lib/api';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import { captureRef } from './lib/referral';
import { brandName } from './lib/brand';
import Header from './components/Header';
import Footer from './components/Footer';
import LiveChatSupport from './components/LiveChatSupport';
import ScrollToTop from './components/ScrollToTop';
import { Toaster } from './components/ui/toaster';
import ServiceNoticeModal from './components/ServiceNoticeModal';
import CustomCodeInjector from './components/CustomCodeInjector';
import WelcomePopup from './components/WelcomePopup';
import PageMarker from './components/PageMarker';
import ViewportLock from './components/ViewportLock';
import { SHELL_IDS } from './lib/domHooks';
import BrowserProtection from './components/BrowserProtection';
import SiteLockedScreen from './components/SiteLockedScreen';
import ServiceUnavailableScreen from './components/ServiceUnavailableScreen';
import EntryInactiveScreen from './components/EntryInactiveScreen';
import { entryDomainClosed, entryDomainHoldsPortal, entryDomainInactive } from './lib/entryDomain';
import {
  adminRoutesVisible, portalVisible,
} from './lib/adminDomain';

import MeritLayout from './themes/merit/MeritLayout';
import MeritHome from './themes/merit/MeritHome';
import BetrunLayout from './themes/betrun/BetrunLayout';
import BetrunHome from './themes/betrun/BetrunHome';
import ZumrutLayout from './themes/zumrut/ZumrutLayout';
import ZumrutHome from './themes/zumrut/ZumrutHome';
import ParisLayout from './themes/paris/ParisLayout';
import ParisHome from './themes/paris/ParisHome';
import HerkulLayout from './themes/herkul/HerkulLayout';
import HerkulHome from './themes/herkul/HerkulHome';
import HerkulSportsPage from './themes/herkul/HerkulSportsPage';
import VegaLayout from './themes/vega/VegaLayout';
import VegaHome from './themes/vega/VegaHome';
import SportsbookFrame from './pages/SportsbookFrame';
import SportsLoginGate from './components/SportsLoginGate';
import { sportsGuestViewAllowed } from './lib/sportsAccess';
import { hostedSportsbooks } from './lib/sportsbookProviders';
import { useActiveTheme } from './lib/activeTheme';

import Home from './pages/Home';
import Slots from './pages/Slots';
import LiveCasino from './pages/LiveCasino';
import Crash from './pages/Crash';
import Sports from './pages/Sports';
import Promotions from './pages/Promotions';
import PromoCode from './pages/PromoCode';
import Wheel from './pages/Wheel';
import Anchor from './pages/Anchor';
import Raffle from './pages/Raffle';
import Prediction from './pages/Prediction';
import NotFound from './pages/NotFound';
import FooterPage from './pages/FooterPage';
import GamePage from './pages/GamePage';
import Sponsorships from './pages/Sponsorships';
import AccountLayout from './pages/AccountLayout';
import Profile from './pages/account/Profile';
import Security from './pages/account/Security';
import KycVerification from './pages/account/KycVerification';
import Deposit from './pages/account/Deposit';
import DepositPayment from './pages/account/DepositPayment';
import Withdraw from './pages/account/Withdraw';
import WithdrawalAccount from './pages/account/WithdrawalAccount';
import Messages from './pages/account/Messages';
import GameHistory from './pages/account/GameHistory';
import AccountActivity from './pages/account/AccountActivity';
import BetCoupons from './pages/account/BetCoupons';
import LossBonus from './pages/account/LossBonus';
import MyBonuses from './pages/account/MyBonuses';
import Affiliate from './pages/account/Affiliate';
import PartnerPortal from './pages/partner/PartnerPortal';

import AdminLayout from './admin/AdminLayout';
import Dashboard from './admin/Dashboard';
import Users from './admin/Users';
import UserProfile from './admin/UserProfile';
import UserActivity from './admin/UserActivity';
import Kyc from './admin/Kyc';
import Finance from './admin/Finance';
import ManualTransactions from './admin/ManualTransactions';
import PaymentMethods from './admin/PaymentMethods';
import Sliders from './admin/Sliders';
import AdminSponsorships from './admin/Sponsorships';
import AdBanners from './admin/AdBanners';
import Bonuses from './admin/Bonuses';
import BulkBonus from './admin/BulkBonus';
import Games from './admin/Games';
import SportsBets from './admin/SportsBets';
import SportsBetDetail from './admin/SportsBetDetail';
import SportsbookBetDetail from './admin/SportsbookBetDetail';
import { V2_ROUTES } from './admin/v2/routes';
import { LEGACY_REDIRECTS } from './admin/lib/navigation';
import AdminSecurity from './admin/Security';
import CallRequests from './admin/CallRequests';
import Affiliates from './admin/Affiliates';
import PromoCodes from './admin/PromoCodes';
import AuditLog from './admin/AuditLog';
import ChatModeration from './admin/ChatModeration';
import Roles from './admin/Roles';
import MeritSidePanel from './admin/MeritSidePanel';
import HomeLayout from './admin/HomeLayout';
import WelcomePopupAdmin from './admin/WelcomePopupAdmin';
import WheelSettings from './admin/WheelSettings';
import FreespinControl from './admin/FreespinControl';
import AnchorSettings from './admin/AnchorSettings';
import Raffles from './admin/Raffles';
import Predictions from './admin/Predictions';
import License from './admin/License';
import Technical from './admin/Technical';
import IntegrationApi from './admin/IntegrationApi';
import AdminUpdates from './admin/Updates';
import AdminLogin from './admin/AdminLogin';

const PublicLayout = ({ children }) => {
  const settings = useSettings();
  const themeId = useActiveTheme();

  // Kurulum kısıtlıysa portalın tamamı kapanır ve kabuk (header, footer, canlı
  // destek) hiç çizilmez: ziyaretçi hangi adresi açarsa açsın tam ekran
  // duyuruyu görür, açık kalan tek yol yönetim panelidir. Bakım modundan önce
  // gelir — ikisi birlikte açıksa daha güçlü olan kapı kazanır.
  if (settings.site_locked) return <SiteLockedScreen />;

  if (settings.maintenance_mode) {
    return (
      <main className="min-h-screen grid place-items-center px-4 bg-[var(--pusu-bg-deep)]">
        <section className="pusu-panel max-w-lg w-full p-8 text-center">
          <div className="text-3xl font-black gold-text mb-3">{brandName(settings)}</div>
          <h1 className="text-xl font-bold mb-2">Kısa bir bakımdayız</h1>
          <p className="text-sm text-gray-400">Size daha iyi hizmet verebilmek için sistem üzerinde çalışıyoruz. Lütfen kısa süre sonra tekrar deneyin.</p>
          {settings.contact_email && <a className="inline-block mt-5 text-sm gold-text" href={`mailto:${settings.contact_email}`}>{settings.contact_email}</a>}
        </section>
      </main>
    );
  }

  // Hazır tasarım şeması kendi kabuğunu getirir: Merit'te sol ikon rayı, üst
  // kategori barı ve dört sütunlu footer; Zümrüt'te başlıklı geniş sol menü ve
  // beş sütunlu footer; Paris'te iki katlı üst menü ve yuvarlak köşeli footer;
  // varsayılanda klasik header/footer.
  if (themeId === 'merit') {
    return (
      <MeritLayout>
        {children}
        <ScrollToTop />
        <LiveChatSupport />
      </MeritLayout>
    );
  }

  // Betrun kuruluma özel şemadır (panelde listelenmez, bkz. lib/siteThemes.js):
  // Merit kabuğunun kopyasıdır ve ondan bağımsız değiştirilir.
  if (themeId === 'betrun') {
    return (
      <BetrunLayout>
        {children}
        <ScrollToTop />
        <LiveChatSupport />
      </BetrunLayout>
    );
  }

  if (themeId === 'zumrut') {
    return (
      <ZumrutLayout>
        {children}
        <ScrollToTop />
        <LiveChatSupport />
      </ZumrutLayout>
    );
  }

  if (themeId === 'paris') {
    return (
      <ParisLayout>
        {children}
        <ScrollToTop />
        <LiveChatSupport />
      </ParisLayout>
    );
  }

  if (themeId === 'herkul') {
    return (
      <HerkulLayout>
        {children}
        <ScrollToTop />
        <LiveChatSupport />
      </HerkulLayout>
    );
  }

  if (themeId === 'vega') {
    return (
      <VegaLayout>
        {children}
        <ScrollToTop />
        <LiveChatSupport />
      </VegaLayout>
    );
  }

  return (
    <>
      <Header />
      {/* Klasik kabukta içeriğin kendi sarmalayıcısı yoktu; beş şemada olan
          `#bhs-main` kimliği burada da bulunmalıdır, yoksa panelden yazılan
          kural şema değişince sessizce çalışmaz olurdu. */}
      <div id={SHELL_IDS.main}>{children}</div>
      <Footer />
      <ScrollToTop />
      <LiveChatSupport />
    </>
  );
};

// Ana sayfanın vitrin düzeni de şemaya bağlıdır.
const THEME_HOMES = {
  merit: MeritHome, zumrut: ZumrutHome, paris: ParisHome, herkul: HerkulHome,
  vega: VegaHome, betrun: BetrunHome,
};

// Spor sayfası normalde tek koddur ve şema onu CSS ile kendi geometrisine
// uydurur. Herkül bilinçli bir istisnadır: referans sportsbook'un düzeni
// (spor listesi rayı + araç paneli) ortak sayfanınkinden tümüyle farklı.
// Veri ve kupon mantığı yine ortak `useSportsbook` hook'undadır.
const THEME_SPORTS = {
  herkul: HerkulSportsPage,
};

const HomeRoute = () => {
  const ThemeHome = THEME_HOMES[useActiveTheme()] || Home;
  return <PublicLayout><ThemeHome /></PublicLayout>;
};

// Spor sayfası sitenin kabuğu içinde açılır (header + footer): oyuncu bakiyesini
// ve menüyü kaybetmeden bahis yapar. Kabuğun dar genişlik sınırı sayfa açıkken
// `body.sb-page` ile kaldırılır — sportsbook üç sütun taşır (bkz. sports.css).
const SportsRoute = () => {
  const settings = useSettings();
  const { user, ready } = useAuth();

  if (!settings.settingsLoaded || !ready) return null;
  if (settings.sports_enabled === false) return <Navigate to="/" replace />;
  // Spor sayfası giriş yapmadan görülebilir mi: karar tek yerdedir ve üç
  // kaynağın (sitenin kendi beslemesi, Betby, Flexrix) hepsinde aynıdır.
  // Kapalıyken ziyaretçi giriş kartını görür; sayfa menüden kaldırılmaz —
  // kaybolan bir bölüm oyuncuya "site bozuldu" gibi görünüyor.
  if (!user && !sportsGuestViewAllowed(settings)) {
    return <PublicLayout><SportsLoginGate /></PublicLayout>;
  }
  // Hazır spor kitabı açıksa /spor onu çizer: sağlayıcı bahsin tamamını
  // (oran, kupon, sonuçlandırma) kendi tarafında yürütür ve sitenin kendi
  // sportsbook'uyla yan yana durması oyuncuya iki ayrı bahis dünyası
  // gösterirdi. Hepsi kapalıyken sayfa sitenin kendi oran beslemesine geri
  // döner. Hangilerinin yayında olduğunu sunucu söyler; birden çoksa oyuncu
  // çerçevenin üstündeki şeritten geçiş yapar.
  const hosted = hostedSportsbooks(settings);
  if (hosted.length) {
    return (
      <PublicLayout>
        <SportsbookFrame providers={hosted} />
      </PublicLayout>
    );
  }
  const ThemeSports = THEME_SPORTS[settings.site_theme] || Sports;
  return <PublicLayout><ThemeSports /></PublicLayout>;
};

const AffiliateAccessRoute = () => {
  const { user, ready } = useAuth();
  if (!ready) return null;
  if (!user?.can_use_affiliate) {
    return <Navigate to="/hesap/profil" replace />;
  }
  return <Affiliate />;
};

// Tanımsız adresler. Kurulum kısıtlıyken duyuru ekranı çizilir — kilit
// "ana sayfa kapalı ama /rastgele-adres açık" gibi bir boşluk bırakmamalıdır.
//
// Kalan her durumda 404 sayfası çizilir. Yönetim paneli ayrı bir alan adına
// kilitlendiğinde ana sitedeki ``/admin`` yolu da buraya düşer ve olmayan
// herhangi bir adresle **aynı** yanıtı verir: panelin varlığına dair iz
// bırakmaz. Eskiden boş ekran çiziliyordu, ziyaretçi siteyi bozuk sanıyordu.
const CatchAllRoute = () => {
  const settings = useSettings();
  const { pathname } = useLocation();
  if (settings.site_locked) return <SiteLockedScreen />;
  // Panel bu adreste duruyorsa oradaki tanımsız adres yöneticinin sorunudur;
  // portal kabuğuyla 404 çizmek panelin içinde yabancı durur.
  if (pathname.startsWith('/admin') && adminRoutesVisible(settings)) return null;
  return <PublicLayout><NotFound /></PublicLayout>;
};

/* Promosyon Kodu hesap menüsünün bir maddesidir ama rotası hesap bölümünün
 * dışındadır (ziyaretçiye de açıktır). Giriş yapmış üye sayfayı hesap
 * kabuğunun **içinde** görür: menüden açtığı sayfada menü kayboluyor ve geri
 * dönmenin yolu kalmıyordu. Kural bir süre tek bir şemaya bağlıydı; kaybolan
 * menü şemadan bağımsız bir çıkmazdır. Ziyaretçi sayfayı kabuksuz görür —
 * hesap kabuğu ona "Giriş Gerekli" derdi.
 */
// Ziyaretçiye açık olup olmadığı panel ayarıdır (Pazarlama & Bonus →
// Promosyon Kodları → Kullanım Kuralları → Sayfa Erişimi,
// `promo_code_guest_view`, varsayılan açık). Kapalıyken giriş yapmamış
// ziyaretçi hesap kabuğunun "Giriş Gerekli" ekranını görür; kodu kullanan
// uçlar zaten oturum ister, yani bu bir para kapısı değil görünürlük kararıdır.
const PromoCodeRoute = () => {
  const { user } = useAuth();
  const settings = useSettings();
  if (user || settings.promo_code_guest_view === false) {
    return (
      <PublicLayout>
        <AccountLayout><PromoCode embedded /></AccountLayout>
      </PublicLayout>
    );
  }
  return <PublicLayout><PromoCode /></PublicLayout>;
};

/**
 * KYC istemeyen bir sayfayı doğrulanmamış üyeden korur.
 *
 * ``deposit`` sayfalarında kapı yalnızca **otomatik** KYC modunda durur:
 * orada her hesap doğrulanmış açılır, doğrulanmamış olan yöneticinin geri
 * aldığı hesaptır. Kayıtta kimlik sorulmayan manuel modda doğrulanmamış hesap
 * normaldir ve para yatırması engellenmez — kural backend'de de aynıdır
 * (finance_router `/deposit`). Çekim her iki modda da doğrulama ister.
 */
const KycProtectedRoute = ({ children, deposit = false }) => {
  const { user, ready } = useAuth();
  const settings = useSettings();
  if (!ready) return null;
  if (deposit && settings.kyc_auto_approve === false) return children;
  if (!user?.kyc_verified) {
    return <Navigate to="/hesap/kyc" replace />;
  }
  return children;
};

/**
 * Rotalar ayarlara bağlıdır: yönetim paneli ayrı bir alan adına taşındıysa
 * portal ile panel aynı pakette olsa da **aynı adreste tanımlanmaz**
 * (bkz. lib/adminDomain.js). Panel adresinde portalın hiçbir sayfası,
 * ana sitede ise kilit onaylandıktan sonra /admin yolları yoktur.
 *
 * Ayarlar gelene kadar hiçbir rota çizilmez: ayrım bilinmeden çizilen kabuk
 * bir kare sonra değişir ve oyuncu portalın açılıp kapandığını görürdü.
 */
const AppRoutes = () => {
  const settings = useSettings();
  // Giriş adresi kararı yola bağlıdır (/admin muaf); uygulama içi geçişte de
  // yeniden hesaplansın diye yönlendiricinin konumu okunur.
  const location = useLocation();
  if (!settings.settingsLoaded) return null;
  // Sunucuya hiç ulaşılamıyorsa (kod güncellemesi sırasında servis yeniden
  // başlar, ters vekil 502/503 döner) elde ayar yoktur: rotalar çizilse de
  // site boş bir kabuk olarak açılır ve oyuncu "site bozuldu" der. Bunun
  // yerine ne olduğunu söyleyen bir ekran çizilir; bağlantı arka planda
  // yoklanır ve sunucu döndüğü anda site kendiliğinden açılır.
  if (settings.serverUnreachable) return <ServiceUnavailableScreen />;
  // Listedeki adres yayına alınmadan siteyi açmaz (bkz. lib/entryDomain.js):
  // taşınma başladıysa hiçbir şey çizilmez, yayında adres yoksa bilgi ekranı.
  // /admin muafiyeti yalnızca panel bu adreste açılıyorsa geçerlidir; değilse
  // /admin portalın 404'üdür ve sitenin kabuğunu çizerdi.
  const here = { ...location, hostname: window.location.hostname };
  const adminRoutes = adminRoutesVisible(settings);
  if (entryDomainHoldsPortal(settings.entry_domain, here, adminRoutes)) {
    if (entryDomainClosed(settings.entry_domain, here, adminRoutes)) {
      return <EntryInactiveScreen closed />;
    }
    return entryDomainInactive(settings.entry_domain, here, adminRoutes)
      ? <EntryInactiveScreen />
      : null;
  }
  const portal = portalVisible(settings);

  return (
    <>
      {portal && <CustomCodeInjector />}
      {/* Panelden yönetilen karşılama penceresi (bkz. lib/welcomePopup.js). */}
      {portal && <WelcomePopup />}
      {/* Panelden yazılan CSS'in "yalnızca bu sayfada" demesinin yolu:
          <body data-bhs-page="…"> (bkz. lib/domHooks.js). */}
      <PageMarker />
      {/* Şemanın mobil yakınlaştırma kilidi (bkz. lib/viewportLock.js). */}
      <ViewportLock settings={settings} />
      <Routes>
            {/* Admin routes (no public header/footer) */}
            {adminRoutes && (
              <Route
              path="/admin/login"
              element={<Navigate to="/" replace state={{ auth: 'login' }} />}
            />
            )}
            {adminRoutes && (
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="users" element={<Users />} />
              <Route path="users/:userId" element={<UserProfile />} />
              <Route path="users/:userId/transactions" element={<UserActivity kind="transactions" />} />
              <Route path="users/:userId/games" element={<UserActivity kind="games" />} />
              <Route path="kyc" element={<Kyc />} />
              <Route path="finance" element={<Finance />} />
              {/* Türü sabit talep sayfaları. Anahtar şart:
                  aynı bileşen olduğu için anahtarsız geçişte süzgeç durumu
                  bir sayfadan ötekine taşınırdı. */}
              <Route path="yatirim-talepleri" element={<Finance key="deposit" requestType="deposit" />} />
              <Route path="cekim-talepleri" element={<Finance key="withdraw" requestType="withdraw" />} />
              <Route path="manual-transactions" element={<ManualTransactions />} />
              <Route path="payment-methods" element={<PaymentMethods />} />
              <Route path="games" element={<Games />} />
              <Route path="sports-bets" element={<SportsBets />} />
              {/* Hazır spor kitabı kuponunun kimliği sağlayıcıya göre
                  benzersizdir, bizim kupon numaramız değildir; bu yüzden
                  sağlayıcı adı yolun parçasıdır ve sabit ``hosted`` ekiyle
                  sitenin kendi kupon rotasından ayrılır. */}
              <Route
                path="sports-bets/hosted/:provider/:betId"
                element={<SportsbookBetDetail />}
              />
              <Route path="sports-bets/:couponId" element={<SportsBetDetail />} />
              <Route path="bonuses" element={<Bonuses />} />
              <Route path="toplu-bonus" element={<BulkBonus />} />
              <Route path="promo-codes" element={<PromoCodes />} />
              <Route path="wheel" element={<WheelSettings />} />
              <Route path="freespin" element={<FreespinControl />} />
              <Route path="anchor" element={<AnchorSettings />} />
              <Route path="cekilisler" element={<Raffles />} />
              <Route path="tahmin-etkinlikleri" element={<Predictions />} />
              <Route path="affiliate" element={<Affiliates />} />
              <Route path="slider" element={<Sliders />} />
              <Route path="sponsorluklar" element={<AdminSponsorships />} />
              <Route path="reklam-kodlari" element={<AdBanners />} />
              <Route path="search" element={<CallRequests />} />
              <Route path="merit-side-panel" element={<MeritSidePanel />} />
              <Route path="ana-sayfa-duzeni" element={<HomeLayout />} />
              <Route path="karsilama-penceresi" element={<WelcomePopupAdmin />} />
              {/* Menünün adresleri: aynı ekranların işe göre bölünmüş ya da
                  birleştirilmiş hâli (bkz. admin/v2/routes.jsx). */}
              {V2_ROUTES.map((route) => (
                <Route key={route.path} path={route.path} element={route.element} />
              ))}
              {/* Kaldırılan eski menü adresleri yeni sayfaya taşınır. */}
              {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
                <Route key={from} path={from.replace('/admin/', '')} element={<Navigate to={to} replace />} />
              ))}
              <Route path="security" element={<AdminSecurity />} />
              <Route path="chat" element={<ChatModeration />} />
              <Route path="audit-log" element={<AuditLog />} />
              <Route path="roles" element={<Roles />} />
              <Route path="license" element={<License />} />
              <Route path="technical" element={<Technical />} />
              <Route path="entegrasyon-api" element={<IntegrationApi />} />
              <Route path="yenilikler" element={<AdminUpdates />} />
            </Route>
            )}

            {/* İş ortağı portalı: panelden bağımsız giriş ekranı ve yalnızca
                ortağın kendi verisini gösteren salt okunur panel (site
                kabuğu çizilmez). */}
            {portal && <Route path="/ortak" element={<PartnerPortal />} />}

            {/* Public site — panel adresinde portal hiç çizilmez */}
            {portal && <Route path="/" element={<HomeRoute />} />}
            <Route path="/spor" element={<SportsRoute />} />
            <Route path="/slotlar" element={<PublicLayout><Slots /></PublicLayout>} />
            <Route path="/canli-casino" element={<PublicLayout><LiveCasino /></PublicLayout>} />
            <Route path="/crash" element={<PublicLayout><Crash /></PublicLayout>} />
            <Route path="/cark" element={<PublicLayout><Wheel /></PublicLayout>} />
            <Route path="/capa" element={<PublicLayout><Anchor /></PublicLayout>} />
            <Route path="/cekilis" element={<PublicLayout><Raffle /></PublicLayout>} />
            <Route path="/etkinlik" element={<PublicLayout><Prediction /></PublicLayout>} />
            <Route path="/etkinlik/:slug" element={<PublicLayout><Prediction /></PublicLayout>} />
            <Route path="/promosyonlar" element={<PublicLayout><Promotions /></PublicLayout>} />
            <Route path="/promosyon-kodu" element={<PromoCodeRoute />} />
            <Route path="/sponsorluk" element={<PublicLayout><Sponsorships /></PublicLayout>} />
            <Route path="/sayfa/:slug" element={<PublicLayout><FooterPage /></PublicLayout>} />
            {/* Oyunun kendi sayfası: yalnızca `gamePage` taşıyan şemada (Betrun)
                açılır, diğerlerinde ana sayfaya yönlenir (bkz. lib/gamePage.js). */}
            <Route path="/oyun/:gameId" element={<PublicLayout><GamePage /></PublicLayout>} />
            <Route path="/hesap" element={<PublicLayout><AccountLayout /></PublicLayout>}>
              <Route path="profil" element={<Profile />} />
              <Route path="guvenlik" element={<Security />} />
              <Route path="kyc" element={<KycVerification />} />
              <Route
                path="para-yatir"
                element={<KycProtectedRoute deposit><Deposit /></KycProtectedRoute>}
              />
              <Route
                path="para-yatir/odeme"
                element={(
                  <KycProtectedRoute deposit><DepositPayment /></KycProtectedRoute>
                )}
              />
              <Route
                path="para-cek"
                element={<KycProtectedRoute><Withdraw /></KycProtectedRoute>}
              />
              <Route
                path="para-cek/hesap"
                element={<KycProtectedRoute><WithdrawalAccount /></KycProtectedRoute>}
              />
              <Route path="mesajlar" element={<Messages />} />
              {/* Hesap Hareketleri: panelden elle yapılan bakiye düzenlemeleri.
                  Yatırım, çekim ve oyun turları kendi sayfalarındadır; eski
                  "İşlemlerim" adresi bu sayfaya bağlandı. */}
              <Route path="islemler" element={<AccountActivity />} />
              <Route path="oyun-gecmisi" element={<GameHistory />} />
              <Route path="bahis-kuponlari" element={<BetCoupons />} />
              <Route path="kayip-bonusu" element={<LossBonus />} />
              <Route path="bonuslarim" element={<MyBonuses />} />
              <Route path="affiliate" element={<AffiliateAccessRoute />} />
            </Route>
            {/* Panel adresinde kök adres panele yönlenir; portalın hiçbir
                sayfası burada tanımlı değildir. */}
            <Route
              path="*"
              element={portal ? <CatchAllRoute /> : <Navigate to="/admin" replace />}
            />
      </Routes>
    </>
  );
};

function App() {
  useEffect(() => {
    client.get('/').catch(() => {});
    // ?ref=XXXXX ile gelen ziyaretçinin referans kodunu yakala
    captureRef();
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <SettingsProvider>
        <BrowserRouter>
          <BrowserProtection />
          <AppRoutes />
          <Toaster />
          {/* Kapalı bir hizmete gelen istek (bkz. lib/api.js) oyuncuya
              küçük bir bildirim yerine açıklayıcı bir pencere gösterir. */}
          <ServiceNoticeModal />
        </BrowserRouter>
        </SettingsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
